import client from '@/shared/apis/client'
import type {
  RemoteGuidanceState,
  RemoteGuidanceSummary,
} from '@/shared/types/yakurekiCore'

// 遠隔服薬指導の依頼と状態の共有 (back log/250)。
// 画面は新画面 (apps/latest) だけ。旧画面 (apps/legacy) は札も録音の線も持たないので、
// 依頼先に設定する 2 店は新画面であることが前提 (back log/250 work-log/13 の既知の制約)

/** 札の文言。患者画面の行と左の一覧で同じものを使う (ここ 1 か所) */
export const REMOTE_STATE_LABEL: Record<RemoteGuidanceState, string> = {
  requested: '依頼中',
  accepted: '受領',
  waiting_patient: '入室待ち',
  seated: '着席',
  in_guidance: '指導中',
  finished: '指導済',
  cancelled: '取り消し',
  declined: 'お断り',
}

/**
 * まだ終えていない札 = 生きていて、指導済の前 (依頼中〜指導中)。
 * 行の操作 (番号札の印刷・受領・指導を始める) を出す線。
 * 「押せる札」の線はここに置き、画面の側で状態名を並べ直さない (back log/250 決定 30・31)
 */
export const isInProgress = (remote: RemoteGuidanceSummary) =>
  remote.is_live && remote.state !== 'finished'

/**
 * 取り消し (頼む店)・断る (遠隔の店) が効く札 = 生きていて、指導中になる前。
 * back の RemoteRequestCancelView / DeclineView が 409 で拒む集合 (指導中・指導済) と対。
 * 線を動かすときは back と一緒に、ここ 1 か所だけ直す (back log/250 決定 27・31)
 */
export const isBeforeGuidance = (remote: RemoteGuidanceSummary) =>
  isInProgress(remote) && remote.state !== 'in_guidance'

/**
 * 頼む店で、この店の記録 (マイク・まとめて録音・録音せずに記載) を止める札。
 * 遠隔の店が指導を終えると通話の音声が処方に入って SOAP になるので、この店で記録すると
 * 片方がもう片方を上書きする (back log/250 決定 26)。
 * 遠隔の店 (incoming) は止めない: 外の通話 (Zoom 等) を選んだ店は Kakeru の録音で通話を
 * 録る (決定 15b・20)。Kakeru の通話とマイクを両方使うと二重になるが、β では運用に任せる
 */
export const blocksOwnRecording = (
  remote: RemoteGuidanceSummary | null | undefined
): remote is RemoteGuidanceSummary =>
  !!remote && remote.direction === 'outgoing' && isInProgress(remote)

export type RemoteCall = {
  livekit_url: string
  livekit_token: string
}

export type RemoteRequestResponse = {
  id: string
  state: RemoteGuidanceState
  /** 指導を始める・ブースの応答だけ。通話が開いていなければ null */
  call?: RemoteCall | null
}

export type BoothResponse = RemoteRequestResponse & {
  queue_number: string | null
  call: RemoteCall | null
}

/** 頼む店: 依頼する */
export const requestRemote = async (yakurekiId: string, note: string) =>
  (
    await client.post<RemoteRequestResponse>(
      `/api/yakureki/${yakurekiId}/remote-request/`,
      { note }
    )
  ).data

export type RemoteAction = 'cancel' | 'accept' | 'decline' | 'start' | 'finish'

/** 頼む店: cancel / 遠隔の店: accept・decline・start・finish */
export const actOnRemoteRequest = async (
  remoteRequestId: string,
  action: RemoteAction,
  note = ''
) =>
  (
    await client.post<RemoteRequestResponse>(
      `/api/remote-requests/${remoteRequestId}/${action}/`,
      { note }
    )
  ).data

/** ブース: 番号札の QR で着席する */
export const boothCheckin = async (qr: string) =>
  (
    await client.post<BoothResponse>('/api/remote-guidance/booth/checkin/', {
      qr,
    })
  ).data

/** ブース: 着席の後の状態 */
export const getBoothStatus = async (remoteRequestId: string) =>
  (
    await client.get<BoothResponse>(
      `/api/remote-guidance/booth/${remoteRequestId}/`
    )
  ).data

/** 番号札の QR の中身 (店・日付・受付番号。決定 12)。back の BoothCheckinView と対 */
export const boothQrValue = (pharmacyId: string, queueNumber: string) => {
  const d = new Date()
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}${String(d.getDate()).padStart(2, '0')}`
  return `kakeru-rg:${pharmacyId}:${ymd}:${queueNumber}`
}
